import graphql from "babel-plugin-relay/macro";
import { Provider, useSetAtom, useStore } from "jotai";
import React, { Suspense, useEffect } from "react";
import { Platform } from "react-native";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { useCompleteLogin } from "@/hooks/useCompleteLogin";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useSyncAnalyticsTrackingPermission } from "@/utils/Analytics/useSyncAnalyticsTrackingPermission";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { useKeyboardBottomInset } from "@whoosh/design-system/src/hooks/useKeyboardBottomInset";

import { SessionClub } from "../../../../Session";
import { VerifiedAuthenticationModalQuery } from "./__generated__/VerifiedAuthenticationModalQuery.graphql";
import { proceedToJoinNewClubStepAtom } from "./atoms/actions";
import {
  VerificationModalCompleteActionAtom,
  VerificationModalStepStateAtom,
  VerificationModalTrackEventAtom,
} from "./atoms/stores";
import { VerifiedAuthenticationModalAtomHydrator } from "./components/VerifiedAuthenticationModalAtomHydrator";
import { VerifiedAuthenticationModalBody } from "./components/VerifiedAuthenticationModalBody/VerifiedAuthenticationModalBody";
import { VerifiedAuthenticationModalHeader } from "./components/VerifiedAuthenticationModalHeader";
import { VerifiedAuthenticationModalStepState } from "./types";

type VerifiedAuthenticationModalProps = {
  onClose?: () => void;
  onComplete: (clubSlug?: string) => void;
  clubSlug?: string;
  hideCloseButton?: boolean;
  initialStep?: VerifiedAuthenticationModalStepState;
  sessionClub: SessionClub | null;
  fallBack?: React.ReactNode;
  alternativeSignIn?: React.ReactNode;
};

export const VerifiedAuthenticationModal = ({
  onClose,
  onComplete,
  clubSlug,
  hideCloseButton,
  initialStep,
  sessionClub,
  fallBack,
  alternativeSignIn,
}: VerifiedAuthenticationModalProps) => {
  const languagePreferenceStore = useStore();
  // Complete authentication in the app store, outside the form's private Provider.
  const onResetPasswordLoginCompleted = useCompleteLogin();
  const [queryRef, loadQuery] =
    useQueryLoader<VerifiedAuthenticationModalQuery>(
      VerifiedAuthenticationModalQueryString
    );
  useEffect(() => {
    loadQuery({}, { fetchPolicy: "store-and-network" });
  }, [loadQuery]);

  return (
    <Suspense fallback={fallBack}>
      <Provider>
        {queryRef ? (
          <VerifiedAuthenticationModalQueryRenderer
            queryRef={queryRef}
            onClose={onClose}
            onComplete={onComplete}
            onResetPasswordLoginCompleted={onResetPasswordLoginCompleted}
            clubSlug={clubSlug}
            hideCloseButton={hideCloseButton}
            initialStep={initialStep}
            languagePreferenceStore={languagePreferenceStore}
            sessionClub={sessionClub}
            alternativeSignIn={alternativeSignIn}
          />
        ) : null}
      </Provider>
    </Suspense>
  );
};

const VerifiedAuthenticationModalQueryRenderer = ({
  queryRef,
  onClose,
  onComplete,
  clubSlug,
  hideCloseButton,
  initialStep,
  languagePreferenceStore,
  onResetPasswordLoginCompleted,
  sessionClub,
  alternativeSignIn,
}: VerifiedAuthenticationModalProps & {
  languagePreferenceStore: ReturnType<typeof useStore>;
  onResetPasswordLoginCompleted: ReturnType<typeof useCompleteLogin>;
  queryRef: PreloadedQuery<VerifiedAuthenticationModalQuery>;
}) => {
  const data = usePreloadedQuery<VerifiedAuthenticationModalQuery>(
    VerifiedAuthenticationModalQueryString,
    queryRef
  );
  useSyncAnalyticsTrackingPermission(data.session);

  const userType = data.session?.user?.__typename;
  const clientType = data.session?.currentClientProfile?.member?.__typename;
  const isLoggedInAndJustNeedsToAgreeToClubPolicies =
    clientType === "AnonymousCustomer" && userType === "MemberUser";
  const proceedToJoinClub = useSetAtom(proceedToJoinNewClubStepAtom);
  /**
   * Handles passing our close/complete functions into atoms so we can call them as needed
   * as well as setting us to the initial screen we should be on if we are just agreeing
   * to policies.
   *
   * This is getting to the point where extracting it into an 'initializer' might start to
   * make sense
   */
  const setVerificationCompleteAction = useSetAtom(
    VerificationModalCompleteActionAtom
  );

  const trackEvent = useTrackEvent();
  const setTrackEvent = useSetAtom(VerificationModalTrackEventAtom);

  useEffect(() => {
    setVerificationCompleteAction(() => {
      return onComplete;
    });
    setTrackEvent(() => trackEvent);

    if (isLoggedInAndJustNeedsToAgreeToClubPolicies) {
      proceedToJoinClub();
    }
  }, [
    isLoggedInAndJustNeedsToAgreeToClubPolicies,
    onComplete,
    proceedToJoinClub,
    setVerificationCompleteAction,
    clubSlug,
    trackEvent,
    setTrackEvent,
  ]);

  const setStepState = useSetAtom(VerificationModalStepStateAtom);

  useEffect(() => {
    if (initialStep) {
      trackEvent("AuthModal.Opened", initialStep);
      setStepState(initialStep);
    } else {
      trackEvent("AuthModal.Opened", {
        step: "Initial",
        backStep: null,
      });
    }
  }, [clubSlug, initialStep, setStepState, trackEvent]);

  const keyboardInset = useKeyboardBottomInset();
  const isKeyboardVisible = Platform.OS === "ios" && keyboardInset > 0;
  const session = data.session;
  return (
    <VStack
      flex={1}
      height="100%"
      paddingBottom={isKeyboardVisible ? undefined : 6}
    >
      <VerifiedAuthenticationModalHeader
        clubSlug={clubSlug}
        onClose={onClose}
        hideCloseButton={hideCloseButton}
        languagePreferenceStore={languagePreferenceStore}
      />
      <VerifiedAuthenticationModalBody
        sessionRef={session ?? null}
        hasAuthenticatedUser={!!session?.user}
        clubSlug={clubSlug}
        sessionClub={sessionClub}
        onComplete={onComplete}
        onResetPasswordLoginCompleted={onResetPasswordLoginCompleted}
        alternativeSignIn={alternativeSignIn}
      />
      {sessionClub ? (
        <VerifiedAuthenticationModalAtomHydrator sessionClub={sessionClub} />
      ) : null}
    </VStack>
  );
};

export const VerifiedAuthenticationModalQueryString = graphql`
  query VerifiedAuthenticationModalQuery {
    session {
      ...useSyncAnalyticsTrackingPermission_session
      user {
        __typename
      }
      currentClientProfile {
        member {
          __typename
        }
      }
      ...VerifiedAuthenticationModalBodyFragment_session
    }
  }
`;
